import { Autocomplete, Form, FormSelect } from "@/base";
import { joiResolver } from "@hookform/resolvers/joi";
import { validationOptions } from "@sr-partner-portal/common";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import type { LanguageSkill } from "../types";
import {
    languageValidationSchema,
} from "../validation";
import { languageSkillLevels } from "./constants";
import { languagesInGerman } from "./languages";
import { useResumeBuilder } from "../context";

type FormData = LanguageSkill;

interface LanguageFormProps {
    isOpen: boolean;
    onClose: () => void;
    onFormSubmit: (data: FormData) => void;
}

const languageFormId = "language-form";

const LanguageForm = ({
    isOpen,
    onClose,
    onFormSubmit,
}: LanguageFormProps) => {
    const { resumeContent } = useResumeBuilder();
    const formController = useForm<FormData>({
        resolver: joiResolver(languageValidationSchema, validationOptions),
        defaultValues: {
            language: "",
            level: 1,
        }
    });

    useEffect(() => {
        if (isOpen) {
            return;
        }

        formController.reset();
    }, [isOpen, formController.reset]);

    const handleSubmit = (data: FormData) => {
        onFormSubmit(data);
        onClose();
    };

    const selectableLanguages = useMemo(() => {
        const usedLanguages = resumeContent.step2.languages.map(
            (lang) => lang.language,
        );
        return languagesInGerman.filter(
            (language) => !usedLanguages.includes(language),
        );
    }, [resumeContent.step2.languages])

    return (
        <Form
            controller={formController}
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
            id={languageFormId}
        >
            <Autocomplete
                fieldName="language"
                autoCompleteItems={selectableLanguages}
                label="Sprache"
            />

            <FormSelect
                fieldName="level"
                label="Kenntnisse"
                items={languageSkillLevels.map((item) => ({
                    id: item.level,
                    name: item.label,
                }))}
                disallowEmptySelection={true}
            />
        </Form>
    );
};

export { LanguageForm, languageFormId };
